// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Container, Icon, Typography, Box, Grid, Divider } from '@material-ui/core';
import _isEmpty from 'lodash/isEmpty';
import { getCardIconByCardType, formatNumberToUSD } from 'utils';
import { convertStringAmountToNumber } from 'modules/clients/utils';
import ReceiptFields from '../ReceiptFields';

const PricingInfo = ({
  classes,
  isVoidOrder,
  isReturnOrder,
  shipmentData,
  transactionType,
  selectedPayments,
  selectedGCPayments,
  cardAmountReceived,
  isBgElevatedPackagingEligible,
  selectedPaymentsAmount,
}) => {
  const { orderTotalWithGiftCard, orderTotalAmt, giftPackaging, giftCard } = shipmentData || {};
  const showTotal = !isReturnOrder && selectedPayments.length > 1;
  const isOnlyGiftCardPayment = !!(!selectedPayments.length && selectedGCPayments.length);

  const renderAmount = (amount, isMultiple = false) => {
    const numberAmount = typeof amount === 'number' ? amount : convertStringAmountToNumber(amount);
    const numberGiftPackaging = convertStringAmountToNumber(giftPackaging);

    /**
     * For BG Brand, if the sumtotal of all regular items in Checkout is more than $700, gift wrap is Free,
     * excluding sale and adjusted items from the sumtotal. (Business asked to override
     * the gift wrapping fee and total in checkout, order receipt and order confirmation page)
     * For more details see: https://neimanmarcus.atlassian.net/browse/OSP-9297
     */
    const displayedAmount = (isBgElevatedPackagingEligible ? numberAmount - numberGiftPackaging : numberAmount).toFixed(
      2,
    );

    return (
      <Typography
        variant={isMultiple ? 'body1' : 'h2'}
        data-testid={isMultiple ? 'order-receipt-additional-payments' : 'order-receipt-total'}
        color={orderTotalAmt && orderTotalAmt.includes('-') ? 'error' : 'textPrimary'}
      >
        {isVoidOrder && '- '}
        {formatNumberToUSD(Number(displayedAmount))}
      </Typography>
    );
  };

  const renderCardInfo = (payment, label) => {
    const { type, networkType, posType, maskedNumber, maskedCardNumber } = payment || {};
    const cardNumber = maskedNumber || maskedCardNumber;
    const cardTypeIcon = getCardIconByCardType(networkType || type || posType);

    return (
      <Box display="flex">
        {label && (
          <Typography variant="body2" className={classes.card} color="textSecondary">
            {label}
          </Typography>
        )}
        <Icon className={classes.card} component={cardTypeIcon} />
        <Typography variant="body1">{cardNumber?.slice(-4) || ''}</Typography>
      </Box>
    );
  };

  const renderSinglePayment = (payment) => {
    let amount = cardAmountReceived || orderTotalWithGiftCard || orderTotalAmt;
    if (isVoidOrder && giftCard) {
      amount = `$${payment.amountReceived}`;
    }

    return (
      <Box display="flex" justifyContent="space-between">
        {renderCardInfo(payment)}
        {renderAmount(amount)}
      </Box>
    );
  };

  const renderMultiplePayments = (payments = []) => (
    <Grid container direction="column" classes={{ root: classes.multiplePayments }}>
      {payments.map((payment) => {
        const { keyIdentifier, amountReceived, amount } = payment || {};
        const displayedAmount = _isEmpty(selectedPaymentsAmount)
          ? amountReceived || amount
          : selectedPaymentsAmount[keyIdentifier].toString();

        return (
          <Box key={keyIdentifier} display="flex" justifyContent="space-between">
            {renderCardInfo(payment)}
            {renderAmount(displayedAmount, true)}
          </Box>
        );
      })}
    </Grid>
  );

  const renderGiftCards = (giftCards = []) => {
    return (
      <Grid container direction="column" classes={{ root: classes.multiplePayments }}>
        {giftCards.map(({ giftCardNumber, selectedBalance, cardType }) => {
          const cardTypeIcon = getCardIconByCardType(cardType);

          return (
            <Box key={giftCardNumber} display="flex" justifyContent="space-between">
              <Box display="flex">
                <Icon className={classes.card} component={cardTypeIcon} />
                <Typography variant="body1">{giftCardNumber?.slice(-4) || ''}</Typography>
              </Box>
              {renderAmount(selectedBalance, giftCards.length > 1)}
            </Box>
          );
        })}
      </Grid>
    );
  };

  const renderTotalRefund = (payment) => (
    <Box display="flex" justifyContent="space-between">
      {renderCardInfo(payment, 'Total Refund')}
      <Typography variant="body2" data-testid="order-receipt-total-refund" color="textPrimary">
        {orderTotalAmt}
      </Typography>
    </Box>
  );

  const renderPayments = () => {
    if (isOnlyGiftCardPayment) {
      return renderGiftCards(selectedGCPayments);
    }

    if (isReturnOrder) {
      return renderTotalRefund(selectedPayments[0]);
    }

    return selectedPayments.length > 1
      ? renderMultiplePayments(selectedPayments)
      : renderSinglePayment(selectedPayments[0]);
  };

  return (
    <Container classes={{ root: cx(isReturnOrder ? classes.returnOrderPricing : classes.pricingInfoContainer) }}>
      <Container disableGutters>
        <ReceiptFields
          isNewStyle
          method={shipmentData}
          hideGiftCards={isOnlyGiftCardPayment}
          storeSaleType={transactionType}
          isRefundOrder={isVoidOrder || isReturnOrder}
          isBgElevatedPackagingEligible={isBgElevatedPackagingEligible}
        />
      </Container>
      <Divider className={classes.divider} />
      {renderPayments()}
      {showTotal && (
        <>
          <Divider className={classes.divider} />
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography className={classes.title} variant="h4" data-testid="order-receipt-total">
              Total
            </Typography>
            {renderAmount(orderTotalWithGiftCard || orderTotalAmt)}
          </Grid>
        </>
      )}
    </Container>
  );
};

PricingInfo.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  isVoidOrder: PropTypes.bool,
  isReturnOrder: PropTypes.bool,
  isBgElevatedPackagingEligible: PropTypes.bool,
  shipmentData: PropTypes.shape({}),
  transactionType: PropTypes.string,
  cardAmountReceived: PropTypes.string,
  selectedPayments: PropTypes.arrayOf(PropTypes.object),
  selectedGCPayments: PropTypes.arrayOf(PropTypes.object),
  selectedPaymentsAmount: PropTypes.shape({}),
};

PricingInfo.defaultProps = {
  classes: {},
  isVoidOrder: false,
  isReturnOrder: false,
  isBgElevatedPackagingEligible: false,
  selectedPayments: [],
  selectedGCPayments: [],
  selectedPaymentsAmount: {},
};

export default PricingInfo;
